import { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import { warmUpAgentSession } from "@/clients/streaming/agent-stream";
import { SafeAreaContent } from "@/components/content/safe-area-content";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import {
  chatMessagesAtom,
  chatOriginAtom,
  chatScrollToMessageIdAtom,
  pendingChatContextAtom,
  pendingChatFoodAtom,
  streamInMessageIdAtom,
  type ChatMessageImage,
} from "@/stores/chat.store";
import { planFoodBadgesAtom, prunePlanFoodBadges } from "@/stores/plan-food-add.store";
import { clearChatWelcomePending, isChatWelcomePending } from "@/utils/chat-welcome-flag";
import { cn } from "@/utils/cn";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { textVariants } from "@/utils/text-variants";
import { IonFooter, IonPage, useIonRouter } from "@ionic/react";
import { format } from "date-fns";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/ui/use-toast";
import { useStickToBottom } from "use-stick-to-bottom";
import { ChatHeader } from "../../components/chat-header";
import { ChatInputBar, type ChatInputBarHandle } from "../../components/chat-input-bar";
import { ChatMealPickerGreeting } from "../../components/chat-meal-picker-greeting";
import { ChatMessageBubble } from "../../components/chat-message-bubble";
import { ChatTypingIndicator } from "../../components/chat-typing-indicator";
import { ChatWelcomeState } from "../../components/chat-welcome-state";
import type { HelpFaqItem } from "../../constants/help-faq";
import { useChatSession } from "../../hooks/use-chat-session";
import { usePrepareCoachChat } from "../../hooks/use-open-coach-chat";
import { useOpenHelpChat } from "../../hooks/use-open-help-chat";
import { buildAddMealToPlanPrompt } from "../../utils/build-add-meal-to-plan-prompt";
import { resolveChatInputPlaceholder } from "../../utils/chat-input-placeholder";
import { resolveChatMessageActions } from "../../utils/chat-message-actions";
import HelpScreen from "../help-screen";

const DownArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface AiChatPageProps {
  /**
   * Optional dismiss handler — when provided, the chevron-down in the
   * header calls this instead of triggering a router back-navigation. Used
   * by `AiChatModal` so the chevron dismisses the modal sheet.
   */
  onClose?: () => void;
}

const AiChatPage = ({ onClose }: AiChatPageProps = {}) => {
  const router = useIonRouter();
  const {
    sessionId,
    messages,
    isTyping,
    isStreaming,
    isStopping,
    sendPrompt,
    retryLastError,
    cancelTurn,
  } = useChatSession();
  const { showError } = useToast();
  const { scrollRef, contentRef, isAtBottom, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  const lastSeenLengthRef = useRef(messages.length);
  const [unreadCount, setUnreadCount] = useState(0);

  const hasStarted = messages.length > 0;
  // The meal-picker greeting ("what would you like to eat next?", Figma node
  // 273:1112) is the default empty state — every chat entry point lands here.
  // The post-onboarding welcome (gated on a "pending" flag set by Meet
  // NutriCoach when onboarding finalizes, so completing/restarting onboarding
  // replays it) is the only exception and overrides the greeting while pending.
  const isWelcome = !hasStarted && isChatWelcomePending();
  const isMealPickerGreeting = !hasStarted && !isWelcome;
  const isBackgroundNoteThinking =
    isTyping && isStreaming && messages.at(-1)?.presentation === "background-note";
  const messageActions = useMemo(
    () => resolveChatMessageActions(messages, isStreaming),
    [isStreaming, messages],
  );

  useEffect(() => {
    trackMixpanelEvent(MixpanelEvents.AI_CHAT_OPENED);
  }, []);

  // The My Plan "just added" badges live only as long as their message is in
  // this transcript. This page is the transcript's only subscriber, so it owns
  // the pruning. An empty list may just be the pre-hydration value, so it is
  // skipped — a purged transcript is handled at badge-tap time instead.
  const setPlanFoodBadges = useSetAtom(planFoodBadgesAtom);
  useEffect(() => {
    if (messages.length === 0) return;
    const liveIds = new Set(messages.map((message) => message.id));
    setPlanFoodBadges((prev) => prunePlanFoodBadges(prev, sessionId, liveIds));
  }, [messages, sessionId, setPlanFoodBadges]);

  // Opened from a badge: once the requested message is rendered, centre it.
  // Two frames so useStickToBottom's initial jump runs first and ours wins.
  const [scrollToMessageId, setScrollToMessageId] = useAtom(chatScrollToMessageIdAtom);
  useEffect(() => {
    if (!scrollToMessageId) return;
    if (!messages.some((message) => message.id === scrollToMessageId)) {
      setScrollToMessageId(null);
      return;
    }
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        scrollRef.current
          ?.querySelector(`[data-message-id="${scrollToMessageId}"]`)
          ?.scrollIntoView({ block: "center" });
        setScrollToMessageId(null);
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollToMessageId, messages, scrollRef, setScrollToMessageId]);

  // Provision the AgentCore microVM ahead of time so the first message lands on
  // a hot VM instead of paying the ~2–3s cold-start penalty. Re-fires when
  // sessionId changes (e.g. after "New Chat") to warm the new session's VM.
  // warmUpAgentSession is fire-and-forget and dedupes internally.
  useEffect(() => {
    warmUpAgentSession(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (isAtBottom) {
      lastSeenLengthRef.current = messages.length;
      setUnreadCount(0);
      return;
    }
    setUnreadCount(Math.max(0, messages.length - lastSeenLengthRef.current));
  }, [messages.length, isAtBottom]);

  const handleBack = () => {
    if (backToHelp) {
      setShowHelp(true);
      setBackToHelp(false);
      return;
    }
    if (onClose) {
      onClose();
      return;
    }
    if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push("/overview", "back");
    }
  };

  const handleHelp = () => setShowHelp(true);

  const handleNewChat = useCallback(async (): Promise<boolean> => {
    const cancelled = await cancelTurn();
    if (!cancelled) {
      showError("Couldn't stop that response. Check your connection and try again.");
      return false;
    }
    setBackToHelp(false);
    return true;
  }, [cancelTurn, showError]);

  const handleStopGenerating = useCallback(async () => {
    const cancelled = await cancelTurn();
    if (!cancelled) {
      showError("Couldn't stop that response. Check your connection and try again.");
    }
  }, [cancelTurn, showError]);

  const [pendingContext, setPendingContext] = useAtom(pendingChatContextAtom);
  const [streamInMessageId, setStreamInMessageId] = useAtom(streamInMessageIdAtom);
  const chatOrigin = useAtomValue(chatOriginAtom);
  const store = useStore();
  const setMessages = useSetAtom(chatMessagesAtom);
  const setOrigin = useSetAtom(chatOriginAtom);

  // Clear the fake-stream target once it finishes revealing so it stays in full
  // and never re-animates on a later re-render / remount.
  const handleStreamInDone = useCallback(() => setStreamInMessageId(null), [setStreamInMessageId]);

  const handleSubmit = useCallback(
    async (text: string, images: ChatMessageImage[]) => {
      // A contextual entry point (e.g. the My Plan meal coach button) may have
      // stashed meal context. Prepend it to the *first* message streamed to the
      // agent only — the visible bubble keeps the user's clean text — then
      // clear it so later messages are unaffected.
      const agentPromptOverride = pendingContext ? `${pendingContext}\n\n${text}` : undefined;
      if (pendingContext) {
        setPendingContext(null);
      }
      clearChatWelcomePending();
      const succeeded = await sendPrompt(text, images, agentPromptOverride);
      scrollToBottom();
      return succeeded;
    },
    [pendingContext, setPendingContext, sendPrompt, scrollToBottom],
  );

  // A meal selected from the food library is sent as a normal chat turn: the
  // visible bubble stays clean while the agent receives the structured meal
  // context needed to perform the plan write. Runs on this hook instance — the
  // one driving the typing indicator / transcript.
  const handleAddMeal = useCallback(
    (visibleText: string, agentPrompt: string) => {
      clearChatWelcomePending();
      void sendPrompt(visibleText, [], agentPrompt);
      scrollToBottom();
    },
    [sendPrompt, scrollToBottom],
  );

  const handleFoodLibraryAdd = useCallback(
    (meal: FoodLibraryMeal) => {
      if (!meal.id) return;

      const nutrition = meal.nutritionalInfo?.perPortion;
      const now = new Date();
      const visibleText = `I would like to add ${meal.name} to my plan`;
      const agentPrompt = buildAddMealToPlanPrompt({
        mealName: meal.name,
        mealId: meal.id,
        day: format(now, "EEEE").toLowerCase(),
        targetDate: format(now, "yyyy-MM-dd"),
        calories: nutrition?.energy?.kcal,
        protein: nutrition?.protein?.grams,
        carbs: nutrition?.carbohydrate?.total?.grams,
        fat: nutrition?.fat?.total?.grams,
      });

      handleAddMeal(visibleText, agentPrompt);
    },
    [handleAddMeal],
  );

  const [pendingChatFood, setPendingChatFood] = useAtom(pendingChatFoodAtom);

  useEffect(() => {
    if (!pendingChatFood) return;

    setPendingChatFood(null);
    handleFoodLibraryAdd(pendingChatFood);
  }, [handleFoodLibraryAdd, pendingChatFood, setPendingChatFood]);

  const handleInterruptAnswer = useCallback(
    (answer: "yes" | "no") => {
      trackMixpanelEvent(MixpanelEvents.AI_CHAT_INTERRUPT_ANSWERED, { answer });
      void handleSubmit(answer, []);
    },
    [handleSubmit],
  );

  const handleJumpToLatest = () => {
    scrollToBottom();
  };

  // Meal-picker greeting fades out as soon as the user starts typing their
  // own prompt, and reappears once the draft is cleared back to empty.
  const inputBarRef = useRef<ChatInputBarHandle>(null);

  const [hasDraftText, setHasDraftText] = useState(false);
  const handleDraftChange = useCallback((draft: string) => setHasDraftText(draft.length > 0), []);

  const handleGreetingMicPress = useCallback(() => {
    void inputBarRef.current?.startMic();
  }, []);

  const handleEditCalories = useCallback(() => {
    inputBarRef.current?.setTextAndFocus("Edit calories: ");
  }, []);

  const [showHelp, setShowHelp] = useState(false);
  const [backToHelp, setBackToHelp] = useState(false);
  const openHelpChat = useOpenHelpChat();
  const handleHelpItemSelect = useCallback(
    (item: HelpFaqItem) => {
      openHelpChat(item);
      setShowHelp(false);
      setBackToHelp(true);
    },
    [openHelpChat],
  );

  // "Ask me something else..." on the embedded Help screen always starts a
  // brand-new chat (unlike the bottom-bar Coach button, which resumes a recent
  // coach chat), then closes Help. Before clearing, stop any reply still
  // arriving — like "New chat" — so its remaining text can't land in the fresh
  // chat.
  const prepareCoachChat = usePrepareCoachChat();
  const handleAskSomethingElse = useCallback(async () => {
    const canReset = await handleNewChat();
    if (!canReset) return;
    prepareCoachChat({ forceNew: true });
    setBackToHelp(false);
    setShowHelp(false);
  }, [handleNewChat, prepareCoachChat]);

  const showJumpPill = hasStarted && !isAtBottom;

  // Dark gradient theme is only active on the idle meal-picker greeting — not
  // when the help screen is open, and not once the user starts typing.
  const showDarkTheme = isMealPickerGreeting && !showHelp && !hasDraftText;
  // The engaged conversation — an active chat, or the greeting once the user
  // starts typing — uses the flat dark chat theme (Figma node 1:482). The
  // welcome and help screens stay light.
  const showDarkEngaged = !showHelp && (hasStarted || (isMealPickerGreeting && hasDraftText));
  const isDarkChat = showDarkTheme || showDarkEngaged;

  const transparentContentStyle = { "--background": "transparent" } as React.CSSProperties;

  return (
    <IonPage>
      {/* Contained purple gradient blob (matches Figma "bg to be blurred"). */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute bottom-[27px] left-1/2 z-0 -translate-x-1/2",
          "h-[245px] w-[341px] rounded-[51px]",
          "bg-gradient-to-b from-[rgba(122,40,238,0.15)] to-[#7a28ee]",
        )}
      />
      {/* Flat dark (engaged conversation) / solid dark (meal-picker greeting) /
          frosted-glass wash (light welcome + help states). The greeting uses the
          solid #130C2D "Onboarding bg" fill from Figma node 632:453 — the gradient
          on the frame sits behind it and is fully occluded. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 z-[1]",
          showDarkEngaged
            ? "bg-[#100f2e]"
            : showDarkTheme
              ? "bg-[#130C2D]"
              : "bg-white/70 backdrop-blur-[27.5px]",
        )}
      />
      <SafeAreaContent scrollY={false} style={transparentContentStyle}>
        {showHelp ? (
          <HelpScreen
            onBack={() => {
              setShowHelp(false);
              if (store.get(chatOriginAtom) === "help") {
                setMessages([]);
                setOrigin("coach");
              }
            }}
            onSelectItem={handleHelpItemSelect}
            onAskSomethingElse={() => void handleAskSomethingElse()}
          />
        ) : (
          <div className="relative z-10 flex h-full flex-col">
            <ChatHeader
              hasStarted={hasStarted}
              onBack={handleBack}
              onNewChat={handleNewChat}
              onHelp={backToHelp ? undefined : handleHelp}
              showTitle={hasStarted || isWelcome || (isMealPickerGreeting && hasDraftText)}
              showActions={hasStarted || (isMealPickerGreeting && hasDraftText)}
              useBackIcon={backToHelp}
              variant={isDarkChat ? "white" : "default"}
            />

            <div className="relative flex flex-1 flex-col overflow-hidden">
              {isMealPickerGreeting ? (
                <div
                  className={cn(
                    "flex-1 overflow-y-auto transition-opacity duration-300 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                    hasDraftText && "pointer-events-none opacity-0",
                  )}
                >
                  <ChatMealPickerGreeting
                    disabled={isTyping}
                    onAddMeal={handleAddMeal}
                    onMicPress={handleGreetingMicPress}
                  />
                </div>
              ) : isWelcome ? (
                <div className="flex-1 overflow-y-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <ChatWelcomeState />
                </div>
              ) : (
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  <div ref={contentRef} className="flex flex-col gap-3 pt-2">
                    {messages.map((message, idx) => {
                      const isLast = idx === messages.length - 1;
                      const nextMessage = messages[idx + 1];
                      const actions = messageActions.get(message.id);
                      return (
                        <ChatMessageBubble
                          key={message.id}
                          messageId={message.id}
                          role={message.role}
                          text={message.text}
                          images={message.images}
                          interrupt={message.interrupt}
                          planFoodsCommitted={message.planFoodsCommitted}
                          actionMode={actions?.mode}
                          separateFromFollowingMessage={Boolean(
                            message.planFoodsCommitted &&
                              ((isLast && isStreaming) ||
                                (nextMessage && !nextMessage.planFoodsCommitted)),
                          )}
                          showInterruptActions={isLast && !isStreaming}
                          onInterruptAnswer={handleInterruptAnswer}
                          isStreaming={Boolean(
                            actions?.isTurnStreaming && message.role === "agent",
                          )}
                          sessionId={sessionId}
                          traceId={actions?.traceId}
                          presentation={message.presentation}
                          onRetry={retryLastError}
                          showRetry={isLast && !isStreaming && message.role === "error"}
                          streamIn={message.id === streamInMessageId}
                          onStreamInDone={
                            message.id === streamInMessageId ? handleStreamInDone : undefined
                          }
                          onEdit={handleEditCalories}
                        />
                      );
                    })}
                    {isTyping && !isBackgroundNoteThinking && <ChatTypingIndicator />}
                  </div>
                </div>
              )}

              {showJumpPill && (
                <button
                  type="button"
                  onClick={handleJumpToLatest}
                  className={cn(
                    "absolute bottom-3 left-1/2 -translate-x-1/2",
                    "flex items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-white shadow-lg",
                    "active:opacity-80",
                    textVariants.footnote,
                  )}
                  aria-label={
                    unreadCount > 0 ? `Jump to ${unreadCount} new messages` : "Jump to latest"
                  }
                >
                  {unreadCount > 0 && (
                    <span>
                      {unreadCount} new {unreadCount === 1 ? "message" : "messages"}
                    </span>
                  )}
                  <DownArrowIcon />
                </button>
              )}
            </div>
          </div>
        )}
      </SafeAreaContent>

      {!showHelp && (
        <IonFooter
          className={cn(
            "ion-no-border bg-transparent",
            // Capacitor SystemBars updates this value as system bars and the
            // keyboard change; iOS falls back to the browser safe-area value.
            "pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))]",
          )}
        >
          <ChatInputBar
            ref={inputBarRef}
            onSubmit={handleSubmit}
            disabled={isStreaming}
            isGenerating={isStreaming}
            isStopping={isStopping}
            onStop={() => void handleStopGenerating()}
            attachmentsDisabled={
              isStreaming ||
              Boolean(messages.at(-1)?.role === "agent" && messages.at(-1)?.interrupt)
            }
            onValueChange={isMealPickerGreeting ? handleDraftChange : undefined}
            variant={isDarkChat ? "dark" : "default"}
            placeholder={resolveChatInputPlaceholder({
              isStreaming,
              hasStarted,
              isMealPickerGreeting,
              chatOrigin,
            })}
          />
        </IonFooter>
      )}
    </IonPage>
  );
};

export default AiChatPage;
